import { useSearchParams } from "react-router-dom";
import { useApi } from "../api/client";
import { submittersPath, type SubmittersResponse } from "../api/usage";

/** `?submitted_by=` value for documents with no recorded submitter. */
export const NO_SUBMITTER = "none";

/**
 * Whose documents a report counts, kept in the URL as `?submitted_by=`.
 *
 * In the URL on every page, including the ones that keep their other filters
 * in local state, so the usage page can link straight into any report already
 * narrowed to one account, and a filtered view survives a refresh.
 */
export function useSubmittedBy(): [string, (value: string) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get("submitted_by") ?? "";
  const set = (next: string) => {
    const p = new URLSearchParams(params);
    if (next) p.set("submitted_by", next);
    else p.delete("submitted_by");
    // A narrower set has fewer pages; staying on page 7 of it shows nothing.
    if (p.has("page")) p.set("page", "1");
    setParams(p);
  };
  return [value, set];
}

/**
 * A console link that keeps the current submitter filter.
 *
 * Moving from one account's agent figures to "View documents" and landing on
 * everyone's documents would present a different population under the same
 * story without saying so.
 */
export function withSubmitter(path: string, submittedBy: string): string {
  if (!submittedBy) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}submitted_by=${encodeURIComponent(submittedBy)}`;
}

/**
 * The "Submitted by" picker every report page shares.
 *
 * Options come from the server's list of accounts that have actually
 * submitted something, so the list has no account that would match nothing.
 * Counts beside each name are all-time, whatever window the page shows.
 */
export function SubmitterFilter({
  value,
  onChange,
  idPrefix,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Keeps the label/select ids unique if two pickers share a page. */
  idPrefix: string;
}) {
  const { data, error } = useApi<SubmittersResponse>(submittersPath());
  const rows = data?.submitters ?? [];
  const accounts = rows.filter((r) => r.user_id);
  const unrecorded = rows.find((r) => !r.user_id);
  // An id from a shared link that is no longer in the list still has to show
  // as selected, or the select silently reads "Everyone" over a filtered page.
  const stale =
    value && value !== NO_SUBMITTER && !accounts.some((r) => r.user_id === value);

  return (
    <span className="date-range">
      <label className="muted" htmlFor={`${idPrefix}-submitter`}>
        Submitted by
      </label>
      <select
        id={`${idPrefix}-submitter`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!data && !error}
        title={
          error
            ? `Could not load the list of submitters: ${error}`
            : "Counts are all-time. API clients are marked “API”."
        }
      >
        <option value="">Everyone</option>
        {accounts.map((r) => (
          <option key={r.user_id} value={r.user_id ?? ""}>
            {r.username ?? r.user_id?.slice(0, 8)}
            {r.via_api ? " · API" : ""} ({r.documents})
          </option>
        ))}
        {unrecorded || value === NO_SUBMITTER ? (
          <option value={NO_SUBMITTER}>
            Not recorded ({unrecorded?.documents ?? 0})
          </option>
        ) : null}
        {stale ? <option value={value}>Selected account</option> : null}
      </select>
    </span>
  );
}
